import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import {
  LayoutDashboard, BookOpen, History, FileText,
  Settings, LogOut, ChevronRight, ShieldCheck, Calculator, Feather, Sun, Moon, LineChart, Menu, X,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/journal", icon: BookOpen, label: "Trade Journal" },
  { to: "/history", icon: History, label: "Trade History" },
  { to: "/reports", icon: FileText, label: "Reports & Insights" },
  { to: "/calculator", icon: Calculator, label: "Calculator" },
  { to: "/thread", icon: Feather, label: "Thread" },
  { to: "/paper-trade", icon: LineChart, label: "Paper Trade" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const closeMobile = () => setMobileOpen(false);

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
      isActive ? "bg-emerald-400/10 text-emerald-400" : "text-zinc-500 hover:text-zinc-200 hover:bg-white/5"
    }`;

  return (
    <div className="flex min-h-screen bg-black" data-testid="app-layout">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 z-30 flex items-center justify-between px-4 bg-zinc-900/90 backdrop-blur-xl border-b border-white/5">
        <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(true)} className="p-2 -ml-2 text-zinc-200 hover:text-white transition-colors" aria-label="Open menu">
          <Menu className="w-5 h-5" />
        </button>
        <span className="text-base font-bold text-white tracking-tight">
          Market<span className="text-emerald-400">Journal</span>
        </span>
        <div className="w-9" />
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-40" onClick={closeMobile} data-testid="mobile-nav-backdrop" />
      )}

      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`w-56 bg-zinc-900/95 md:bg-zinc-900/80 backdrop-blur-xl flex flex-col fixed h-full z-50 border-r border-white/5 transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="h-16 px-5 flex items-center justify-between border-b border-white/5">
          <span className="text-lg font-bold text-white tracking-tight">
            Market<span className="text-emerald-400">Journal</span>
          </span>
          <button data-testid="mobile-menu-close" onClick={closeMobile} className="md:hidden p-1 text-zinc-400 hover:text-white transition-colors" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeMobile}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={navLinkClass}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              onClick={closeMobile}
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? "bg-emerald-400/10 text-emerald-400" : "text-emerald-400/40 hover:text-emerald-400 hover:bg-white/5"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" />
              Admin Panel
            </NavLink>
          )}
        </nav>

        <div className="p-3 border-t border-white/5">
          <button data-testid="theme-toggle" onClick={toggleTheme}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-all duration-200 mb-1">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl hover:bg-white/5 transition-colors">
                <Avatar className="w-7 h-7">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-emerald-400/20 text-emerald-400 text-xs font-semibold">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-xs font-medium text-zinc-300 truncate">{user?.name || "User"}</p>
                </div>
                <ChevronRight className="w-3 h-3 text-zinc-600" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl bg-zinc-900 border-white/10">
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-zinc-400 hover:text-white focus:text-white focus:bg-white/5">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 min-w-0 md:ml-56 min-h-screen pt-14 md:pt-0 overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}
